import { useStore } from '@xyflow/react'
import { useCallback } from 'react'
import { COLLAPSE_ZOOM_THRESHOLD, getZoomScale } from '../utils/zoomUtils'

/**
 * Result from useZoomState hook
 */
export interface ZoomState {
  /** Whether the viewport is in collapsed/minimized state */
  isCollapsed: boolean
  /** Capped zoom scale for normal view (max 2.0) */
  zoomScale: number
  /** Uncapped zoom scale for collapsed view (true 1/zoom) */
  zoomScaleRaw: number
}

// Quantize zoom to reduce re-renders
// We round to nearest 0.05 which gives ~20 steps between 0.1 and 1.5
const ZOOM_STEP = 0.05

function quantizeZoom(zoom: number): number {
  return Math.round(zoom / ZOOM_STEP) * ZOOM_STEP
}

/**
 * Custom hook that efficiently provides zoom-related state for nodes.
 *
 * The problem: useViewport() triggers re-renders on EVERY viewport change,
 * including every frame during pan/zoom. With 15 nodes, that's 15 * 60fps = 900 re-renders/second.
 *
 * The solution: This hook uses useStore with a custom equality function that only
 * triggers re-renders when:
 * 1. isCollapsed state changes (crossing COLLAPSE_ZOOM_THRESHOLD)
 * 2. zoomScale changes by a meaningful amount (quantized to ZOOM_STEP)
 *
 * For pan operations (no zoom change), this hook causes ZERO re-renders.
 */
export function useZoomState(): ZoomState {
  // Selector that extracts and computes zoom state
  const selector = useCallback((state: { transform: [number, number, number] }): ZoomState => {
    const zoom = state.transform[2]
    const isCollapsed = zoom < COLLAPSE_ZOOM_THRESHOLD
    const zoomScale = getZoomScale(zoom)
    const zoomScaleRaw = 1 / zoom

    return {
      isCollapsed,
      zoomScale,
      zoomScaleRaw,
    }
  }, [])

  // Equality function that only triggers re-render when values meaningfully change
  const equalityFn = useCallback((a: ZoomState, b: ZoomState): boolean => {
    // Always re-render if collapsed state changes
    if (a.isCollapsed !== b.isCollapsed) {
      return false
    }

    // Quantize zoom scales - only re-render if they cross a threshold
    const quantizedScaleA = quantizeZoom(a.zoomScale)
    const quantizedScaleB = quantizeZoom(b.zoomScale)

    // Also quantize zoomScaleRaw for minimized view counter-scaling
    const quantizedRawA = quantizeZoom(a.zoomScaleRaw)
    const quantizedRawB = quantizeZoom(b.zoomScaleRaw)

    return quantizedScaleA === quantizedScaleB && quantizedRawA === quantizedRawB
  }, [])

  return useStore(selector, equalityFn)
}

/**
 * Even more optimized hook that ONLY re-renders when collapsed state changes.
 * Use this when you only need isCollapsed and can handle zoom scale via CSS.
 */
export function useIsCollapsed(): boolean {
  const selector = useCallback((state: { transform: [number, number, number] }): boolean => {
    return state.transform[2] < COLLAPSE_ZOOM_THRESHOLD
  }, [])

  const equalityFn = useCallback((a: boolean, b: boolean): boolean => a === b, [])

  return useStore(selector, equalityFn)
}
